import { useEffect, useMemo } from "react";
import { BsHouseDoor } from "../../../assets/icons/vander";
import {
  useQuizStore,
  usePatientSurgeryStore,
  useFormEventStore,
  useFormMessages,
} from "../../hooks";
import {
  VideoPlayer,
  ErrorMessage,
  LoadingData,
  MainButton,
  CancelButton,
} from "../../ui";
import { QuizProgressBar } from "../progress";
import { PatientSurgeryTab } from "../tabs";
import { QuizGroup, QuizTerms } from "../quiz";
import { VirtualAssistantChat } from "../chat";
import { PatientInfoCard } from "./";

export const QuizCard = () => {
  // Datos del hook ----------------------------------------//
  const {
    // Props
    patientSurgery,
  } = usePatientSurgeryStore();

  const {
    // Props
    isLoading,
    message,
  } = useFormEventStore();

  const {
    // Props
    isStarted,
    introVideoSeen,
    showQuestions,
    answers,
    error,
    termsAccepted,

    // Methods
    setIntroVideoSeen,
    goBackToStart,
    validateAllQuestions,
    calculatePercentage,
    handleSubmitAnswers,
  } = useQuizStore();

  const { assessmentQuestionGroups, introVideoUrl } =
    patientSurgery.data.procedure.assessment || {};

  // States ------------------------------------------------//
  const answersSignature = useMemo(
    () => answers.map((a) => `${a.questionId}:${!!a.value}`).join("|"),
    [answers]
  );

  // Formulario --------------------------------------------//
  const { setFormSubmitted, showErrorMessage, messageText } = useFormMessages({
    message,
  });

  // Funciones ---------------------------------------------//

  // Efectos -----------------------------------------------//
  useEffect(() => {
    const handleBeforeUnload = () => {
      goBackToStart();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [goBackToStart]);

  useEffect(() => {
    if (answers.length > 0) {
      calculatePercentage();
      validateAllQuestions();
    }
  }, [answersSignature]);

  if (!isStarted) {
    return (
      <div className="container-xl py-4">
        <nav aria-label="breadcrumb" className="d-inline-block mb-4">
          <ul className="breadcrumb rounded mb-0 p-0">
            <li className="breadcrumb-item" aria-current="page">
              <BsHouseDoor className="icons-sm" /> Paciente
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Perfil
            </li>
          </ul>
        </nav>
        <div className="vstack gap-4">
          <h1 className="mb-0">Información del paciente</h1>
          <PatientInfoCard />
          <PatientSurgeryTab />
        </div>
      </div>
    );
  }

  if (!introVideoSeen && introVideoUrl) {
    return (
      <div className="container">
        <VideoPlayer
          videoSrc={introVideoUrl}
          markVideoAsSeen={() => setIntroVideoSeen(true)}
          containerClass="d-flex justify-content-center align-items-center vh-100"
          maxHeight="95vh"
          height="95vh"
        />
      </div>
    );
  }

  if (!showQuestions) return null;

  return (
    <>
      <div className="container-xl py-4">
        <div className="mx-auto" style={{ maxWidth: "720px", width: "100%" }}>
          <nav aria-label="breadcrumb" className="d-inline-block ">
            <ul className="breadcrumb rounded mb-0 p-0">
              <li className="breadcrumb-item" aria-current="page">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none"
                  onClick={goBackToStart}
                >
                  Volver
                </button>
              </li>
            </ul>
          </nav>

          <div className="vstack gap-4">
            <div
              className="bg-light py-3 z-2"
              style={{ position: "sticky", top: "0" /* zIndex: "10" */ }}
            >
              <h1 className="mb-3">Encuesta sobre procedimiento</h1>
              <QuizProgressBar />
            </div>
            {assessmentQuestionGroups?.map((group) => (
              <QuizGroup key={group.questionGroup.id} group={group} />
            ))}

            <QuizTerms />

            {isLoading && <LoadingData />}
            {showErrorMessage && <ErrorMessage>{messageText}</ErrorMessage>}
            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="d-flex justify-content-end gap-2">
              <CancelButton
                type="button"
                onClick={goBackToStart}
                disabled={isLoading}
              >
                Cancelar
              </CancelButton>

              <MainButton
                type="button"
                onClick={() => handleSubmitAnswers(setFormSubmitted)}
                disabled={!termsAccepted || isLoading}
              >
                {isLoading ? "Enviando..." : "Enviar encuesta"}
              </MainButton>
            </div>
          </div>
        </div>
      </div>

      <VirtualAssistantChat />
    </>
  );
};
